import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import {
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHighlightImage,
  ArticleHeading,
} from "@/components/primitives/article";
import { PageContainer } from "@/components/primitives/layout";
import { renderMdx } from "@/components/content/render-mdx";
import {
  getAllChangelogEntries,
  type ChangelogEntry,
} from "@/lib/content/changelog";

const getChangelogEntries = createServerFn({ method: "GET" }).handler(
  async () => {
    return getAllChangelogEntries();
  },
);

export const Route = createFileRoute("/changelog/")({
  head: () => ({
    meta: [
      { title: "Changelog - Scoopulator" },
      { name: "description", content: "See what's new in Scoopulator." },
    ],
  }),
  loader: () => getChangelogEntries(),
  component: ChangelogPage,
});

function ChangelogPage() {
  const entries = Route.useLoaderData();

  return (
    <PageContainer>
      <ArticleContainer>
        <ArticleHeader>
          <ArticleHeading>What&apos;s New</ArticleHeading>
          <ArticleDescription>
            Keep track of the latest updates and improvements to Scoopulator in
            the changelog.
          </ArticleDescription>
        </ArticleHeader>
        <div className="flex flex-col divide-y">
          {entries.map((entry: ChangelogEntry) => (
            <article key={entry.slug} className="py-10 first:pt-0">
              <p className="text-muted-foreground mb-1 text-sm">
                {new Date(entry.publishedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <h2 className="mb-6 text-2xl leading-10 font-semibold">
                <a
                  href={entry.url}
                  className="relative -ml-6 block pl-6 no-underline"
                >
                  <span className="decoration-primary underline [text-decoration-style:wavy] decoration-2 underline-offset-8">
                    {entry.title}
                  </span>
                </a>
              </h2>
              {entry.image ? (
                <ArticleHighlightImage src={entry.image} alt={entry.title} />
              ) : null}
              {renderMdx(entry.body)}
            </article>
          ))}
        </div>
      </ArticleContainer>
    </PageContainer>
  );
}
